import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../common/components/Navbar'; // <--- Imported Layout
import {
  Title,
  Paper,
  Table,
  Group,
  Button,
  Modal,
  TextInput,
  Select,
  PasswordInput,
  Badge,
  Switch,
  LoadingOverlay,
  Stack,
  Text
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconUserPlus, IconSearch } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import axios from '../../api/axios';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [search, setSearch] = useState('');

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      role: 'admin',
    },
    validate: {
      name: (value) => (value.length < 2 ? 'Name must have at least 2 letters' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
    },
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (values) => {
    setSubmitLoading(true);
    try {
      await axios.post('/api/users', values);
      Swal.fire('Success', 'User created successfully', 'success');
      setOpened(false);
      form.reset();
      fetchUsers();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    try {
      setUsers(users.map(u => u._id === id ? { ...u, isActive: !currentStatus } : u));
      await axios.put(`/api/users/${id}/status`, { isActive: !currentStatus });
    } catch (error) {
      setUsers(users.map(u => u._id === id ? { ...u, isActive: currentStatus } : u));
      Swal.fire('Error', error.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => 
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase())
    );
  }, [users, search]);

  const rows = filteredUsers.map((user) => (
    <Table.Tr key={user._id}>
      <Table.Td>
        <Text fw={500}>{user.name}</Text>
      </Table.Td>
      <Table.Td>{user.email}</Table.Td>
      <Table.Td>
        <Badge 
          color={user.role === 'super_admin' ? 'violet' : user.role === 'admin' ? 'blue' : 'gray'}
          variant="light"
        >
          {user.role.replace('_', ' ').toUpperCase()}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge color={user.isActive ? 'green' : 'red'} variant="dot">
          {user.isActive ? 'Active' : 'Disabled'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Switch 
          checked={user.isActive}
          onChange={() => handleStatusChange(user._id, user.isActive)}
          color="teal"
          disabled={user.role === 'super_admin'}
          aria-label="Toggle status"
        />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Layout> 
      <Stack>
        <Group justify="space-between">
          <Title order={2}>User Management</Title>
          <Button leftSection={<IconUserPlus size={18} />} color="teal" onClick={() => setOpened(true)}>
            Add New Admin
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group mb="md">
            <TextInput
              placeholder="Search by name or email..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: 400 }}
            />
          </Group>

          <div style={{ position: 'relative', minHeight: 200 }}>
            <LoadingOverlay visible={loading} />
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length > 0 ? rows : (
                  <Table.Tr>
                    <Table.Td colSpan={5} align="center">No users found</Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </Paper>

        <Modal opened={opened} onClose={() => setOpened(false)} title="Create New System User" centered>
          <form onSubmit={form.onSubmit(handleAddUser)}>
            <Stack>
              <TextInput
                label="Full Name"
                placeholder="John Doe"
                required
                {...form.getInputProps('name')}
              />
              <TextInput
                label="Email Address"
                placeholder="admin@balihai.com"
                required
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                placeholder="******"
                required
                {...form.getInputProps('password')}
              />
              <Select
                label="Role"
                placeholder="Pick one"
                data={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'super_admin', label: 'Super Admin' },
                  { value: 'staff', label: 'Staff' }
                ]}
                required
                {...form.getInputProps('role')}
              />
              <Button type="submit" color="teal" fullWidth mt="md" loading={submitLoading}>
                Create User
              </Button>
            </Stack>
          </form>
        </Modal>
      </Stack>
    </Layout>
  );
};

export default UserManagement;